import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Crown, Loader2, Copy, Save, RotateCcw, Plus, Trash2, Check, AlertTriangle, Wand2, FileText, Image as ImageIcon, PenLine, Download, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ImageCreator } from "@/components/marketing/ImageCreator";

const CONTENT_TYPES = [
  { v: "feed_post", l: "Instagram feed post" },
  { v: "reel_caption", l: "Instagram Reel caption" },
  { v: "story_copy", l: "Instagram Story copy" },
  { v: "collab_opportunity", l: "Collaboration opportunity copy" },
  { v: "campaign", l: "General promotional campaign" },
];
const TONES = ["Friendly", "Professional", "Fun", "Premium", "Bold", "Community-focused"];
const EMPTY = { promoting: "", content_type: "feed_post", offer: "", audience: "", cta: "", tone: "Friendly", details: "", avoid: "", extra_context: "" };
const copy = (t) => { navigator.clipboard?.writeText(t || ""); toast.success("Copied"); };

function PackView({ pack, onChange, editable }) {
  const set = (k, v) => onChange({ ...pack, [k]: v });
  const Section = ({ label, field, multiline, testid }) => (
    <div className="rounded-xl border border-zinc-200 bg-white p-3">
      <div className="mb-1 flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wide text-zinc-500">{label}</Label>
        <button data-testid={`copy-${testid}`} onClick={() => copy(pack[field])} className="text-zinc-400 hover:text-emerald-600"><Copy className="h-3.5 w-3.5" /></button>
      </div>
      {editable
        ? (multiline
            ? <Textarea data-testid={`field-${testid}`} value={pack[field] || ""} onChange={(e) => set(field, e.target.value)} rows={3} />
            : <Input data-testid={`field-${testid}`} value={pack[field] || ""} onChange={(e) => set(field, e.target.value)} />)
        : <p className="whitespace-pre-line text-sm text-zinc-800">{pack[field]}</p>}
    </div>
  );
  return (
    <div className="space-y-3" data-testid="content-pack">
      <div className="rounded-xl border border-zinc-200 bg-white p-3">
        <Label className="text-xs uppercase tracking-wide text-zinc-500">Headline / hook options</Label>
        <div className="mt-1 space-y-1.5">
          {(pack.headlines || []).map((h, i) => (
            <div key={i} className="flex items-center gap-2">
              {editable
                ? <Input data-testid={`field-headline-${i}`} value={h} onChange={(e) => { const arr = [...pack.headlines]; arr[i] = e.target.value; set("headlines", arr); }} />
                : <p className="flex-1 text-sm text-zinc-800">{i + 1}. {h}</p>}
              <button onClick={() => copy(h)} className="text-zinc-400 hover:text-emerald-600"><Copy className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
      <Section label="Main caption" field="main_caption" multiline testid="main" />
      <Section label="Alternative caption" field="alt_caption" multiline testid="alt" />
      <Section label="Call to action" field="call_to_action" testid="cta" />
      <div className="rounded-xl border border-zinc-200 bg-white p-3">
        <div className="mb-1 flex items-center justify-between"><Label className="text-xs uppercase tracking-wide text-zinc-500">Hashtags</Label>
          <button data-testid="copy-hashtags" onClick={() => copy((pack.hashtags || []).join(" "))} className="text-zinc-400 hover:text-emerald-600"><Copy className="h-3.5 w-3.5" /></button></div>
        <p className="text-sm text-emerald-700">{(pack.hashtags || []).join("  ")}</p>
      </div>
      <Section label="Suggested on-image text" field="on_image_text" testid="onimage" />
      <Section label="Visual direction" field="visual_direction" multiline testid="visual" />
      {(pack.missing_info || []).length > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="missing-info">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div><b>Add these details for stronger copy:</b><ul className="mt-1 list-disc pl-4">{pack.missing_info.map((m, i) => <li key={i}>{m}</li>)}</ul></div>
        </div>
      )}
    </div>
  );
}

export default function MarketingStudio() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [view, setView] = useState("form"); // form | result | drafts
  const [mode, setMode] = useState("copy"); // copy | image
  const [imageInitial, setImageInitial] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pack, setPack] = useState(null);
  const [brief, setBrief] = useState(null);
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [images, setImages] = useState([]);
  const [savedId, setSavedId] = useState(null);
  const [savedFilter, setSavedFilter] = useState("all"); // all | images | content
  const [preview, setPreview] = useState(null); // a saved AI image being previewed
  const [brokenImages, setBrokenImages] = useState({});

  const loadStatus = () => api.get("/ai/marketing/status").then((r) => { setStatus(r.data); setCtx(r.data.business_context); });
  const loadDrafts = () => api.get("/ai/marketing/drafts").then((r) => setDrafts(r.data.drafts || []));
  const loadImages = () => api.get("/ai/marketing/images").then((r) => setImages(r.data.images || [])).catch(() => {});
  useEffect(() => { loadStatus().catch(() => {}); loadDrafts().catch(() => {}); loadImages(); }, []);

  const generate = async (isRegen = false) => {
    if (isRegen && !window.confirm("Regenerating will use another one of your monthly AI generations. Continue?")) return;
    setBusy(true);
    try {
      const r = await api.post("/ai/marketing/generate", form);
      setPack(r.data.pack); setBrief(r.data.brief); setStatus(r.data.status); setSavedId(null); setEditing(false);
      setView("result");
      toast.success("Content pack ready");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed — not counted"); }
    finally { setBusy(false); }
  };

  const saveDraft = async (statusVal = "draft") => {
    try {
      const title = form.promoting?.slice(0, 80) || "Untitled campaign";
      if (savedId) {
        await api.put(`/ai/marketing/drafts/${savedId}`, { title, content_type: form.content_type, brief, pack, status: statusVal });
      } else {
        const r = await api.post("/ai/marketing/drafts", { title, content_type: form.content_type, brief, pack, status: statusVal });
        setSavedId(r.data.id);
      }
      toast.success(statusVal === "completed" ? "Saved as completed" : "Saved to your drafts");
      loadDrafts();
    } catch { toast.error("Could not save"); }
  };
  const deleteDraft = async (id) => { await api.delete(`/ai/marketing/drafts/${id}`); toast.success("Deleted"); loadDrafts(); };
  const deleteImage = async (id) => {
    if (!window.confirm("Delete this image? This cannot be undone.")) return;
    await api.delete(`/ai/marketing/images/${id}`); toast.success("Image deleted"); setPreview(null); loadImages();
  };
  const downloadImage = async (img) => {
    try {
      const path = (img.url || "").replace(/^\/api/, "");
      const res = await api.get(path, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `nearfluence-image-${img.id.slice(0, 8)}.png`; a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Image downloaded");
    } catch { toast.error("Could not download the image"); }
  };
  const openDraft = (d) => {
    if (d.pack?.kind === "image" || ["square", "portrait", "story"].includes(d.content_type)) {
      setMode("image");
      setImageInitial({ id: d.id, brief: d.brief || {}, design: d.pack?.design || {} });
      setView("form");
      return;
    }
    setMode("copy");
    setPack(d.pack); setBrief(d.brief); setForm({ ...EMPTY, ...(d.brief || {}), content_type: d.content_type }); setSavedId(d.id); setEditing(false); setView("result");
  };
  const copyAll = () => {
    if (!pack) return;
    const t = [`HEADLINES:\n${(pack.headlines || []).map((h, i) => `${i + 1}. ${h}`).join("\n")}`,
      `MAIN CAPTION:\n${pack.main_caption}`, `ALT CAPTION:\n${pack.alt_caption}`,
      `CTA:\n${pack.call_to_action}`, `HASHTAGS:\n${(pack.hashtags || []).join(" ")}`,
      `ON-IMAGE TEXT:\n${pack.on_image_text}`, `VISUAL DIRECTION:\n${pack.visual_direction}`].join("\n\n");
    copy(t);
  };

  const isFree = status && !status.is_pro;

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 pt-6 pb-32 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-600 text-white"><Wand2 className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display text-2xl font-bold text-zinc-900">AI Marketing Assistant</h1>
            <p className="text-sm text-zinc-500">Answer a few questions and Nearfluence will turn your ideas into ready-to-edit marketing content.</p>
          </div>
        </div>

        {status && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-indigo-200 bg-indigo-50/60 px-3 py-2" data-testid="ai-allowance">
            <span className="flex items-center gap-1.5 text-sm font-medium text-indigo-800"><Sparkles className="h-4 w-4" />
              {status.is_pro ? <>{status.remaining_this_month} of {status.allowance} monthly generations left{status.bonus_available > 0 ? ` · +${status.bonus_available} bonus` : ""}</> : "AI generation is a Business Pro feature"}</span>
            <div className="flex gap-2">
              <button data-testid="tab-form" onClick={() => setView("form")} className={`rounded-full px-3 py-1 text-xs font-semibold ${view !== "drafts" ? "bg-indigo-600 text-white" : "bg-white text-indigo-700"}`}>Create</button>
              <button data-testid="tab-drafts" onClick={() => { setView("drafts"); loadDrafts(); loadImages(); }} className={`rounded-full px-3 py-1 text-xs font-semibold ${view === "drafts" ? "bg-indigo-600 text-white" : "bg-white text-indigo-700"}`}>Saved ({drafts.length + images.length})</button>
            </div>
          </div>
        )}

        {isFree && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="pro-upsell">
            <p className="flex items-center gap-2 font-semibold text-amber-800"><Crown className="h-4 w-4" /> Unlock the AI Marketing Assistant with Business Pro</p>
            <p className="mt-1 text-sm text-amber-700">Generate polished captions, hooks, hashtags and visual direction in seconds — 3 generations every month on Pro.</p>
            <Button data-testid="upsell-upgrade" disabled className="mt-3 rounded-full bg-indigo-600 opacity-70">Upgrade to Pro — Coming soon</Button>
          </div>
        )}

        {status && status.is_pro && view !== "drafts" && (
          <div className="mt-3 flex gap-2 rounded-xl bg-zinc-100 p-1" data-testid="studio-mode-switch">
            <button data-testid="mode-copy" onClick={() => { setMode("copy"); setView("form"); }} className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold ${mode === "copy" ? "bg-white text-indigo-700 shadow" : "text-zinc-500"}`}><PenLine className="mr-1.5 inline h-4 w-4" /> Write copy</button>
            <button data-testid="mode-image" onClick={() => { setMode("image"); setView("form"); setImageInitial(null); }} className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold ${mode === "image" ? "bg-white text-indigo-700 shadow" : "text-zinc-500"}`}><ImageIcon className="mr-1.5 inline h-4 w-4" /> Create an image</button>
          </div>
        )}

        {mode === "image" && view !== "drafts" && (
          <ImageCreator status={status} initial={imageInitial}
            onStatusChange={(s) => { setStatus(s); loadImages(); }} onSavedDraft={() => { loadDrafts(); loadImages(); }} />
        )}

        {/* Guided form */}
        {mode === "copy" && view === "form" && (
          <div className="mt-4 space-y-3" data-testid="brief-form">
            {ctx && (
              <div className="rounded-xl border border-zinc-200 bg-white p-3 text-sm" data-testid="business-context">
                <p className="font-semibold text-zinc-800">Using your business profile</p>
                <p className="text-zinc-500">{[ctx.business_name, ctx.business_category, ctx.location].filter(Boolean).join(" · ") || "—"}</p>
                <p className="mt-1 text-xs text-zinc-400">Correct any details in your <button onClick={() => navigate("/business/profile")} className="underline">business profile</button> before generating.</p>
              </div>
            )}
            <div><Label>What are you promoting? *</Label><Input data-testid="f-promoting" value={form.promoting} onChange={(e) => setForm({ ...form, promoting: e.target.value })} className="mt-1" placeholder="e.g. New weekend brunch menu" /></div>
            <div><Label>What type of content?</Label>
              <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
                <SelectTrigger data-testid="f-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTENT_TYPES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Main offer or message</Label><Textarea data-testid="f-offer" value={form.offer} onChange={(e) => setForm({ ...form, offer: e.target.value })} className="mt-1" rows={2} placeholder="e.g. 2-for-1 pancakes all weekend" /></div>
            <div><Label>Target audience</Label><Input data-testid="f-audience" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="mt-1" placeholder="e.g. Local families and students" /></div>
            <div><Label>What action should customers take?</Label><Input data-testid="f-cta" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} className="mt-1" placeholder="e.g. Book a table / visit this weekend" /></div>
            <div><Label>Preferred tone</Label>
              <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                <SelectTrigger data-testid="f-tone" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Important details (dates, prices, locations, terms)</Label><Textarea data-testid="f-details" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="mt-1" rows={2} /></div>
            <div><Label>Anything to avoid? (optional)</Label><Input data-testid="f-avoid" value={form.avoid} onChange={(e) => setForm({ ...form, avoid: e.target.value })} className="mt-1" /></div>
            <div><Label>Additional business context (optional)</Label><Textarea data-testid="f-extra" value={form.extra_context} onChange={(e) => setForm({ ...form, extra_context: e.target.value })} className="mt-1" rows={2} /></div>
            <Button data-testid="generate-btn" disabled={busy || isFree || (status && (status.total_available ?? status.remaining_this_month) === 0)} onClick={() => generate(false)} className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1.5 h-4 w-4" /> Let’s create your campaign</>}
            </Button>
            {status && (status.total_available ?? status.remaining_this_month) === 0 && !isFree && <p className="text-center text-xs text-amber-600">No generations left this month — resets on {status?.reset_date || "the 1st"}.</p>}
          </div>
        )}

        {/* Result */}
        {mode === "copy" && view === "result" && pack && (
          <div className="mt-4">
            <div className="mb-3 flex flex-wrap gap-2">
              <Button size="sm" data-testid="copy-all" variant="outline" onClick={copyAll} className="rounded-full"><Copy className="mr-1 h-3.5 w-3.5" /> Copy all</Button>
              <Button size="sm" data-testid="toggle-edit" variant="outline" onClick={() => setEditing((v) => !v)} className="rounded-full"><FileText className="mr-1 h-3.5 w-3.5" /> {editing ? "Done editing" : "Edit"}</Button>
              <Button size="sm" data-testid="save-draft" variant="outline" onClick={() => saveDraft("draft")} className="rounded-full"><Save className="mr-1 h-3.5 w-3.5" /> Save draft</Button>
              <Button size="sm" data-testid="regenerate" variant="outline" onClick={() => generate(true)} disabled={busy || (status && (status.total_available ?? status.remaining_this_month) === 0)} className="rounded-full"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Regenerate</Button>
              <Button size="sm" data-testid="new-campaign" variant="outline" onClick={() => { setForm(EMPTY); setPack(null); setView("form"); }} className="rounded-full"><Plus className="mr-1 h-3.5 w-3.5" /> New</Button>
            </div>
            <PackView pack={pack} onChange={setPack} editable={editing} />
            <p className="mt-3 flex items-center gap-1.5 rounded-lg bg-zinc-100 px-3 py-2 text-xs text-zinc-500" data-testid="review-notice">
              <Check className="h-3.5 w-3.5 text-emerald-600" /> Please review AI-generated content and confirm all details are accurate before publishing.
            </p>
          </div>
        )}

        {/* Drafts + saved images */}
        {view === "drafts" && (
          <div className="mt-4" data-testid="drafts-list">
            <div className="mb-3 flex gap-2" data-testid="saved-filters">
              {[["all", "All"], ["images", "Images"], ["content", "Content"]].map(([v, l]) => (
                <button key={v} data-testid={`saved-filter-${v}`} onClick={() => setSavedFilter(v)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${savedFilter === v ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 border border-zinc-200"}`}>
                  {l}{v === "images" ? ` (${images.length})` : v === "content" ? ` (${drafts.length})` : ""}
                </button>
              ))}
            </div>

            {/* AI image gallery */}
            {savedFilter !== "content" && images.length > 0 && (
              <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="image-gallery">
                {images.map((img) => (
                  <div key={img.id} data-testid={`saved-image-${img.id}`} className="group relative overflow-hidden rounded-xl border border-zinc-200 bg-white">
                    <button onClick={() => setPreview(img)} className="block aspect-square w-full">
                      {brokenImages[img.id]
                        ? <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-zinc-100 text-center text-[10px] text-zinc-400"><ImageIcon className="h-5 w-5" /><span>Image unavailable</span></div>
                        : <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt="" loading="lazy" onError={() => setBrokenImages((b) => ({ ...b, [img.id]: true }))} className="h-full w-full object-cover" />}
                    </button>
                    <div className="absolute inset-x-0 bottom-0 flex justify-end gap-1 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                      <button data-testid={`preview-image-${img.id}`} onClick={() => setPreview(img)} className="rounded-full bg-white/90 p-1.5 text-zinc-700 hover:bg-white"><Eye className="h-3.5 w-3.5" /></button>
                      <button data-testid={`download-image-${img.id}`} onClick={() => downloadImage(img)} className="rounded-full bg-white/90 p-1.5 text-emerald-700 hover:bg-white"><Download className="h-3.5 w-3.5" /></button>
                      <button data-testid={`delete-image-${img.id}`} onClick={() => deleteImage(img.id)} className="rounded-full bg-white/90 p-1.5 text-rose-600 hover:bg-white"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Copy drafts + image design drafts */}
            {savedFilter !== "images" && (
              <div className="space-y-2">
                {drafts.length === 0 ? <p className="text-sm text-zinc-400">No saved content yet.</p>
                  : drafts.map((d) => (
                    <div key={d.id} data-testid={`draft-${d.id}`} className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-3">
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openDraft(d)}>
                        <p className="truncate font-semibold text-zinc-900">{(d.pack?.kind === "image" || ["square","portrait","story"].includes(d.content_type)) && <ImageIcon className="mr-1 inline h-3.5 w-3.5 text-indigo-500" />}{d.title}</p>
                        <p className="text-xs text-zinc-500">{(CONTENT_TYPES.find((c) => c.v === d.content_type) || {}).l || `Image · ${d.content_type}`} · {new Date(d.created_at).toLocaleDateString()} · <span className="capitalize">{d.status}</span></p>
                      </div>
                      <button data-testid={`open-draft-${d.id}`} onClick={() => openDraft(d)} className="text-indigo-600 hover:underline text-sm">Open</button>
                      <button data-testid={`delete-draft-${d.id}`} onClick={() => deleteDraft(d.id)} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
              </div>
            )}

            {savedFilter === "images" && images.length === 0 && <p className="text-sm text-zinc-400">No saved images yet. Generate one in the “Create an image” tab.</p>}
          </div>
        )}

        {/* Image preview modal */}
        <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="image-preview-dialog">
            <DialogHeader><DialogTitle>Saved image</DialogTitle></DialogHeader>
            {preview && (
              <div>
                <img src={`${process.env.REACT_APP_BACKEND_URL}${preview.url}`} alt="" className="w-full rounded-xl border border-zinc-200" />
                <p className="mt-2 text-xs text-zinc-500">{preview.format} · {preview.width}×{preview.height} · {new Date(preview.created_at).toLocaleString()}</p>
              </div>
            )}
            <DialogFooter className="flex-row justify-end gap-2">
              <Button variant="outline" data-testid="preview-delete" onClick={() => preview && deleteImage(preview.id)} className="rounded-full text-rose-600"><Trash2 className="mr-1 h-4 w-4" /> Delete</Button>
              <Button data-testid="preview-download" onClick={() => preview && downloadImage(preview)} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Download className="mr-1 h-4 w-4" /> Download</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <MobileNav />
    </div>
  );
}
